import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CommitHeatmap from '../../components/CommitHeatmap/CommitHeatmap';
import CycleTimeSection from '../../components/dora/CycleTimeSection';
import { CycleTimeSummaryMetricCards } from '../../components/dora/CycleTimeSummaryMetricCard';
import { FeatureGate } from '../../contexts/FeatureFlagContext';

function formatDeploysPerDay(deploymentFreq, deploymentWindowDays) {
  if (deploymentFreq == null || !Number.isFinite(deploymentFreq) || deploymentWindowDays <= 0) {
    return '—';
  }
  const perDay = deploymentFreq / deploymentWindowDays;
  return perDay < 0.1 ? perDay.toFixed(2) : perDay.toFixed(1);
}

function formatLeadTimeLabel(meanLeadMins) {
  if (meanLeadMins == null || !Number.isFinite(meanLeadMins)) return '—';
  if (meanLeadMins < 60) return `${Math.round(meanLeadMins)} min`;
  const h = Math.floor(meanLeadMins / 60);
  const m = Math.round(meanLeadMins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function StreamDetailOverviewTab({
  orgId,
  streamId,
  projects,
  streamReposFlat,
  streamReposLoading,
  deploymentLoading,
  deploymentDora,
  deploymentWindowDays,
  leadTimeLoading,
  leadTimeDoraMeta,
  activeStreamTab,
  onGoToMetrics,
  onGoToProjects,
}: any) {
  const navigate = useNavigate();

  const repoCountByProjectId = useMemo(() => {
    const counts = new Map();
    for (const r of streamReposFlat) {
      const pid = r._projectId;
      if (pid) counts.set(pid, (counts.get(pid) ?? 0) + 1);
    }
    return counts;
  }, [streamReposFlat]);

  const deploysPerDay = formatDeploysPerDay(
    deploymentDora?.deploymentFrequency,
    deploymentWindowDays
  );
  const leadTimeLabel = formatLeadTimeLabel(leadTimeDoraMeta?.meanLeadTimeMinutes);

  return (
    <div role="tabpanel" id="stream-tabpanel-overview" aria-labelledby="stream-tab-overview">
      <div className="card-mdl-static px-4 py-3 mb-4 flex flex-wrap items-center gap-x-2 gap-y-2">
        <span className="text-slate-500 text-sm">
          Projects
          <span className="font-bold text-slate-100 text-sm ml-1">{projects.length}</span>
        </span>
        <span className="w-px bg-dark-elevated self-stretch mx-1" aria-hidden />
        <span className="text-slate-500 text-sm">
          Repositories
          <span className="font-bold text-slate-100 text-sm ml-1">
            {streamReposLoading ? '…' : streamReposFlat.length}
          </span>
        </span>
        <span className="w-px bg-dark-elevated self-stretch mx-1" aria-hidden />
        <span className="text-slate-500 text-sm">
          Deploys/day
          <span className="font-bold text-slate-100 text-sm ml-1">
            {deploymentLoading ? '…' : deploysPerDay}
          </span>
        </span>
        <span className="w-px bg-dark-elevated self-stretch mx-1" aria-hidden />
        <span className="text-slate-500 text-sm">
          Lead time
          <span className="font-bold text-slate-100 text-sm ml-1">
            {leadTimeLoading ? '…' : leadTimeLabel}
          </span>
        </span>
        <button
          type="button"
          onClick={onGoToMetrics}
          className="ml-auto text-xs text-vivid-red font-medium hover:underline shrink-0"
        >
          Full metrics →
        </button>
      </div>

      <FeatureGate flag="ui.streams.cycleTimeSection">
        <div className="section-block-mdl">
          <CycleTimeSection
            className="!mt-0"
            fetchScope={orgId && streamId ? { orgId, streamId } : undefined}
          />
        </div>
        <div className="section-block-mdl">
          <CycleTimeSummaryMetricCards
            fetchScope={orgId && streamId ? { orgId, streamId } : undefined}
          />
        </div>
      </FeatureGate>

      <div className="mb-4">
        <CommitHeatmap
          scope="stream"
          organisationId={orgId}
          streamId={streamId}
          title="Activity"
          fetchEnabled={activeStreamTab === 'overview' && !!orgId && !!streamId}
        />
      </div>

      <div className="card-mdl-static p-4">
        <span className="text-sm font-semibold text-slate-100">Projects</span>
        <div className="mt-3 divide-y divide-dark-border">
          {projects.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">No projects in this stream yet.</p>
          ) : (
            projects.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => navigate(`/streams/${streamId}/projects/${p.id}`)}
                className="flex w-full items-center justify-between gap-2 py-2.5 text-left hover:text-vivid-red transition-colors"
              >
                <span className="font-medium text-slate-100 truncate">{p.name}</span>
                <span className="text-sm text-slate-400 shrink-0">
                  {streamReposLoading ? '…' : `${repoCountByProjectId.get(p.id) ?? 0} repos`}
                </span>
              </button>
            ))
          )}
        </div>
        {projects.length > 0 && (
          <button
            type="button"
            onClick={onGoToProjects}
            className="mt-3 text-xs text-vivid-red font-medium hover:underline"
          >
            View all projects →
          </button>
        )}
      </div>
    </div>
  );
}
